import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@habitech/call-to-action';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import Icon from 'src/components/common/Icon';
import SelectableRangeCard from 'src/components/Retention/CommercialCart/SelectableRangeCard';
import { SUBCATEGORY_VIEW_CONFIG } from 'src/components/Retention/CommercialCart/ProducsSelectable/CommercialCart.constants';
import {
    selectPortfolioCreateError,
    selectPortfolioCreating,
} from 'src/redux/user_portfolio/user_portfolio.selectors';
import { createHighlightedSlots } from 'src/redux/user_portfolio/thunks';
import { useLazyPortfolio } from '../hooks/useLazyPortfolio';
import {
    Actions,
    CardsGrid,
    Intro,
    SaveError,
    SlotsRoot,
    Toast,
} from './CommercialSlots.styles';

const EMPTY_COPY = 'Busca un cliente por correo electrónico para crear sus slots comerciales.';

const SUCCESS_MESSAGE = 'Los slots comerciales se crearon correctamente';

type SlotType = 'destacado' | 'prime';

interface SlotCardConfig {
    type: SlotType;
    title: string;
    description: string;
}

/** Tarjetas disponibles para crear slots, en el mismo estilo del carrito comercial. */
const SLOT_CARDS: SlotCardConfig[] = [
    {
        type: 'destacado',
        title: 'Destacado',
        description: 'Posiciona tus propiedades en los primeros lugares',
    },
    {
        type: 'prime',
        title: 'Prime',
        description: 'Prioridad máxima dentro de los anuncios destacados',
    },
];

/** Meses de vigencia disponibles en el dropdown (1 a 12). */
const MONTHS_OPTIONS = Array.from({ length: 12 }, (_, i) => {
    const value = String(i + 1);
    return { value, label: i === 0 ? '1 mes' : `${value} meses` };
});

interface CardState {
    selected: boolean;
    quantity: number;
    months: string;
    quantityError: boolean;
}

/** Cantidad por defecto de un tipo de slot (cae a 1 si no hay config registrada). */
const defaultQuantity = (type: SlotType): number =>
    SUBCATEGORY_VIEW_CONFIG[type]?.quantity.default ?? 1;

const buildInitialState = (): Record<SlotType, CardState> => ({
    destacado: {
        selected: false,
        quantity: defaultQuantity('destacado'),
        months: '1',
        quantityError: false,
    },
    prime: {
        selected: false,
        quantity: defaultQuantity('prime'),
        months: '1',
        quantityError: false,
    },
});

/**
 * Tab "Slots comerciales": crea slots comerciales (destacado/prime) para el
 * usuario activo. Se muestran las tarjetas Prime y Destacado (mismo estilo del
 * carrito comercial); en cada una se define la cantidad de slots y la vigencia en
 * meses (dropdown). Al confirmar se dispara un POST `commercial_panel/highlighted`
 * por cada tarjeta seleccionada.
 */
const CommercialSlots = () => {
    const dispatch = useAppDispatch();
    const { submittedQuery, error } = useLazyPortfolio();
    const creating = useAppSelector(selectPortfolioCreating);
    const createError = useAppSelector(selectPortfolioCreateError);

    const [cards, setCards] = useState<Record<SlotType, CardState>>(buildInitialState);
    const [toast, setToast] = useState<string | null>(null);

    // El toast de confirmación se cierra solo tras unos segundos.
    useEffect(() => {
        if (!toast) return undefined;
        const timer = window.setTimeout(() => setToast(null), 6000);
        return () => window.clearTimeout(timer);
    }, [toast]);

    const updateCard = (type: SlotType, patch: Partial<CardState>) => {
        setCards((prev) => ({ ...prev, [type]: { ...prev[type], ...patch } }));
    };

    /**
     * Selecciona/deselecciona un tipo de slot. Solo puede haber un tipo activo:
     * al seleccionar uno, el otro queda deseleccionado.
     */
    const toggleCardSelection = (type: SlotType, next: boolean) => {
        setCards((prev) => {
            const updated = {} as Record<SlotType, CardState>;
            SLOT_CARDS.forEach(({ type: t }) => {
                updated[t] = { ...prev[t], selected: t === type ? next : false };
            });
            return updated;
        });
    };

    const selectedTypes = useMemo(
        () => SLOT_CARDS.map((c) => c.type).filter((type) => cards[type].selected),
        [cards]
    );

    const canSubmit =
        !creating &&
        selectedTypes.length > 0 &&
        selectedTypes.every((type) => {
            const card = cards[type];
            return Number.isInteger(card.quantity) && card.quantity > 0 && !card.quantityError;
        });

    const handleSubmit = () => {
        if (!canSubmit) return;
        Promise.all(
            selectedTypes.map((type) =>
                dispatch(
                    createHighlightedSlots({
                        numberSlots: cards[type].quantity,
                        months: Number(cards[type].months),
                        prime: type === 'prime',
                    })
                ).unwrap()
            )
        )
            .then(() => {
                setCards(buildInitialState());
                setToast(SUCCESS_MESSAGE);
            })
            .catch(() => {
                /* El error se refleja vía createError en el estado. */
            });
    };

    if (!submittedQuery) {
        return <SlotsRoot>{EMPTY_COPY}</SlotsRoot>;
    }

    if (error) {
        return <SlotsRoot>{error}</SlotsRoot>;
    }

    return (
        <SlotsRoot>
            <Intro>
                <Icon icon="pcom-icon-information-filled" size={16} />
                Selecciona el tipo de slot e indica la cantidad y los meses de vigencia
            </Intro>

            <CardsGrid>
                {SLOT_CARDS.map(({ type, title, description }) => {
                    const card = cards[type];
                    const viewConfig = SUBCATEGORY_VIEW_CONFIG[type];
                    if (!viewConfig) return null;
                    return (
                        <div key={type} data-id={`commercial-slot-card-${type}`}>
                            <SelectableRangeCard
                                selected={card.selected}
                                onToggle={(next) => toggleCardSelection(type, next)}
                                borderColor={viewConfig.colors.borderColor}
                                backgroundColor={viewConfig.colors.backgroundColor}
                                checkColor={viewConfig.colors.checkColor}
                                icon={viewConfig.icon}
                                title={title}
                                description={description}
                                dropdownPlacement="after"
                                onSectionError={(_sectionId, hasError) =>
                                    updateCard(type, { quantityError: hasError })
                                }
                                dropdownSection={{
                                    id: 'months',
                                    label: 'Meses',
                                    value: card.months,
                                    options: MONTHS_OPTIONS,
                                    onChange: (value) => updateCard(type, { months: value }),
                                }}
                                sections={[
                                    {
                                        id: 'quantity',
                                        label: 'Cantidad',
                                        value: card.quantity,
                                        onChange: (value) => updateCard(type, { quantity: value }),
                                        minAllowed: viewConfig.quantity.min,
                                        maxAllowed: viewConfig.quantity.max,
                                        showMaxInLabel: false,
                                    },
                                ]}
                            />
                        </div>
                    );
                })}
            </CardsGrid>

            {createError && <SaveError>{createError}</SaveError>}

            <Actions>
                {toast && (
                    <Toast data-id="commercial-slots-toast" role="status">
                        <Icon icon="pcom-icon-check-circle" size={18} />
                        <span>{toast}</span>
                    </Toast>
                )}
                <Button
                    type="button"
                    variant="primary"
                    size="large"
                    originType="pcom"
                    dataId="button-create-commercial-slots"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    loading={creating}
                >
                    Crear slots
                </Button>
            </Actions>
        </SlotsRoot>
    );
};

export default CommercialSlots;
